import { RadioGroup, RadioGroupItem, cn } from '@parrot-software/design-system';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form-v7';
import { Beer, Bike, CupSoda, Utensils } from '@parrot-software/design-system/icons';

import { StepLayout } from '@onboarding/presentation/components/shared';
import type { WizardFormValues } from '@onboarding/presentation/types';

type SectionConfig = {
  titleKey: string;
  icon: ReactNode;
  options: string[];
};

const SECTIONS: SectionConfig[] = [
  {
    titleKey: 'steps.restaurantType.sections.quickService',
    icon: <CupSoda className="text-primary" size={24} />,
    options: ['bowls', 'cafe', 'hamburgers', 'bakery', 'taquerias'],
  },
  {
    titleKey: 'steps.restaurantType.sections.fullService',
    icon: <Utensils className="text-primary" size={24} />,
    options: ['casualDining', 'fineDining', 'seafood', 'sushi'],
  },
  {
    titleKey: 'steps.restaurantType.sections.barEntertainment',
    icon: <Beer className="text-primary" size={24} />,
    options: ['bar', 'entertainment'],
  },
  {
    titleKey: 'steps.restaurantType.sections.deliverySpecialized',
    icon: <Bike className="text-primary" size={24} />,
    options: ['pizzeria', 'darkKitchen'],
  },
];

/**
 * Step 1 of the onboarding wizard: Restaurant type selection.
 * Renders options grouped in 4 sections with icons and titles.
 */
export const RestaurantTypeStep = () => {
  const { t } = useTranslation('onboarding');
  const { control } = useFormContext<WizardFormValues>();

  const sections = useMemo(
    () =>
      SECTIONS.map((section) => ({
        ...section,
        title: t(section.titleKey),
        items: section.options.map((key) => ({
          value: key,
          label: t(`steps.restaurantType.options.${key}`),
          description: t(`steps.restaurantType.options.${key}Description`),
        })),
      })),
    [t],
  );

  return (
    <StepLayout description={t('steps.restaurantType.description')} title={t('steps.restaurantType.title')}>
      <Controller
        control={control}
        name="restaurantType"
        render={({ field }) => (
          <RadioGroup className="flex flex-col gap-7" onValueChange={field.onChange} value={field.value}>
            {sections.map((section) => (
              <div className="flex flex-col gap-3" key={section.titleKey}>
                <div className="flex items-center gap-2">
                  {section.icon}
                  <span className="text-base font-semibold leading-6 text-foreground">{section.title}</span>
                </div>
                <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
                  {section.items.map((option) => {
                    const isSelected = field.value === option.value;
                    return (
                      <label
                        className={cn(
                          'flex cursor-pointer items-center gap-3 rounded-lg border p-4 shadow-xs transition-colors',
                          isSelected ? 'border-primary' : 'border-border hover:border-gray-300',
                        )}
                        key={option.value}
                      >
                        <div className="flex flex-1 flex-col gap-0.5">
                          <span className="text-base font-medium leading-6 text-foreground">{option.label}</span>
                          <span className="text-sm font-normal leading-5 text-muted-foreground">
                            {option.description}
                          </span>
                        </div>
                        <RadioGroupItem id={option.value} value={option.value} />
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </RadioGroup>
        )}
      />
    </StepLayout>
  );
};
